import styled from "styled-components";

/*
 * The access screens and the refused-call notice. Self-contained: a lazy page's sheet is not
 * loaded when one of these is the first thing drawn, so nothing here leans on a page's CSS.
 * The values are the app's error dialog's (ErrorModal.styles.ts): Noto Sans, #36424A text,
 * #004D9E primary pill, #C4C7C8 outline pill, 15px/500 button labels.
 */

/** The brand-only header: the logo and "CSM", and no control of any kind. */
export const BrandName = styled.span`
  color: #36424a;
  font-family: "Noto Sans", sans-serif;
  font-size: 20px;
  font-weight: 300;
  line-height: 33px;
`;

export const BrandLogo = styled.div`
  && {
    cursor: default;
  }
`;

/** Fills the scroll area and centres the panel in it. */
export const Centre = styled.div`
  box-sizing: border-box;
  min-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
`;

export const Panel = styled.section`
  box-sizing: border-box;
  width: 100%;
  max-width: 560px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 32px;
  font-family: "Noto Sans", sans-serif;
  color: #36424a;
  text-align: left;
  -webkit-font-smoothing: antialiased;

  @media (max-width: 600px) {
    padding: 24px 0;
  }

  /* The wait fades in late, so an answer that arrives at once shows nothing at all. */
  &[data-state="loading"] {
    animation: access-gate-appear 200ms ease-out 300ms both;
  }

  @keyframes access-gate-appear {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &[data-state="loading"] {
      animation: none;
    }
  }
`;

export const Heading = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 300;
  line-height: 32px;
  color: #36424a;
  text-wrap: balance;

  /* Focus is moved here on entry so a screen reader starts at the heading. It is not a
     control, so it draws no ring. */
  &:focus {
    outline: none;
  }
`;

export const Body = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  color: #36424a;
`;

export const Detail = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: #595959;
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 8px;

  @media (max-width: 600px) {
    & > button {
      flex: 1 1 100%;
    }
  }
`;

const pill = `
  box-sizing: border-box;
  min-height: 44px;
  padding: 10px 24px;
  border-radius: 30px;
  font-family: "Noto Sans", sans-serif;
  font-size: 15px;
  font-weight: 500;
  line-height: 22px;
  cursor: pointer;
  touch-action: manipulation;
  transition-property: background-color, border-color, transform;
  transition-duration: 120ms;
  transition-timing-function: ease-out;

  &:focus-visible {
    outline: 2px solid #36424a;
    outline-offset: 2px;
  }

  &:active:not([aria-disabled="true"]) {
    transform: scale(0.96);
  }

  &[aria-disabled="true"] {
    cursor: default;
    opacity: 0.6;
  }

  @media (prefers-reduced-motion: reduce) {
    transition-property: background-color, border-color;

    &:active:not([aria-disabled="true"]) {
      transform: none;
    }
  }
`;

export const PrimaryButton = styled.button`
  ${pill}
  min-width: 112px;
  background: #004d9e;
  color: #ffffff;
  border: 1px solid #004d9e;

  @media (hover: hover) and (pointer: fine) {
    &:hover:not([aria-disabled="true"]) {
      background: #003d7d;
      border-color: #003d7d;
    }
  }
`;

export const SecondaryButton = styled.button`
  ${pill}
  background: transparent;
  color: #36424a;
  border: 1px solid #c4c7c8;

  @media (hover: hover) and (pointer: fine) {
    &:hover:not([aria-disabled="true"]) {
      border-color: #36424a;
    }
  }
`;

/**
 * The refused-call notice. Bottom left, above the footer, and clear of the assistant's bubble
 * on the right (24px + 56px + a gap), so it covers neither the header nor that control.
 */
export const NoticeDock = styled.div`
  position: fixed;
  left: 24px;
  bottom: 72px;
  z-index: 5000;
  width: max-content;
  max-width: min(400px, calc(100vw - 112px));
  pointer-events: none;

  @media (max-width: 600px) {
    left: 16px;
    max-width: calc(100vw - 104px);
  }
`;

export const NoticeCard = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 12px 12px 16px;
  background: #ffffff;
  border: 1px solid #c4c7c8;
  border-left: 4px solid #004d9e;
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  font-family: "Noto Sans", sans-serif;
  color: #36424a;
  pointer-events: auto;

  /* Empty and invisible while there is nothing to say, but still in the page. */
  &[data-shown="false"] {
    padding: 0;
    border: 0;
    background: transparent;
    box-shadow: none;
    pointer-events: none;
  }
`;

export const NoticeText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  padding-top: 2px;

  p {
    margin: 0;
    font-size: 15px;
    line-height: 22px;
    overflow-wrap: break-word;
  }

  p + p {
    margin-top: 4px;
    font-size: 13px;
    line-height: 18px;
    color: #595959;
  }
`;

export const DismissButton = styled.button`
  ${pill}
  flex: 0 0 auto;
  padding: 10px 16px;
  background: transparent;
  color: #36424a;
  border: 1px solid #c4c7c8;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      border-color: #36424a;
    }
  }
`;
